// LineJobFormDrawer's reads and its one write (finance-real, the user 2026-10-22): Setup's ceiling, which sets the least
// margin % the form takes, the server's cap for the price and margin % typed (TD-102), and making the job: on its client,
// for a team invoice active on the client picked with the person's claim on it, or for a draft invoice active and on it.
// The cap is read once the price and margin % rest for COST_MS, not on every keystroke (the S6 review); a cleared or
// invalid one clears it at once.
import { useEffect, useState } from "react";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { useClaimSettings } from "../../hooks/useJobClaims";
import { useCreateJob, useCreateJobOnInvoice, useJobCost } from "../../hooks/useLineJobs";
import { useNewJobOnTeamInvoice } from "../../hooks/useTeamInvoices";
import type { JobInvoice, LineJob, LineJobCost, LineJobInput, TeamInvoice, TeamInvoiceNewJobInput } from "../../types";

export interface LineJobFormDrawerModel {
  /** The least margin % a job keeps: 100 less Setup's ceiling, or 0 for the own client, a job that pays all to the team,
   * and until the ceiling is read. */
  least: number;
  /** The margin % typed, once it is a whole number from `least` to 100; null otherwise. */
  marginPct: number | null;
  /** The cap at that price and margin %, read once both are valid. */
  cost: UseQueryResult<LineJobCost>;
  /** `cost` answers the price and margin % typed now: both rested, and not the last pair's answer kept on screen. */
  costCurrent: boolean;
  create: UseMutationResult<LineJob, Error, LineJobInput>;
  /** For a team invoice: the job and the person's claim on it, both or neither. */
  createForTeam: UseMutationResult<TeamInvoice, Error, { invoiceId: string; input: TeamInvoiceNewJobInput }>;
  /** For a draft invoice: the job, active, on it, both or neither. */
  createForInvoice: UseMutationResult<JobInvoice, Error, { invoiceId: string; input: LineJobInput }>;
}

/** How long the price and margin % rest before the server is asked for their cap. */
export const COST_MS = 300;

/** `value` once it stopped changing for `ms`; null at once, so a cleared field never reads the last cap. */
function useSettled(value: number | null, ms: number): number | null {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    if (value === null) {
      setSettled(null);
      return;
    }
    const timer = window.setTimeout(() => setSettled(value), ms);
    return () => window.clearTimeout(timer);
  }, [value, ms]);
  return value === null ? null : settled;
}

/** `priceMinor` is the price typed once it is above zero, `typedMargin` the margin % typed once it is a whole number,
 * `paysAll` the Pays all to the team box. */
export function useLineJobFormDrawer(clientId: string, own: boolean, priceMinor: number | null, typedMargin: number | null, paysAll = false): LineJobFormDrawerModel {
  const create = useCreateJob(clientId);
  const createForTeam = useNewJobOnTeamInvoice();
  const createForInvoice = useCreateJobOnInvoice();
  const settings = useClaimSettings();
  // Until Setup's ceiling is read, the server's refusal is the check.
  const least = own || paysAll || !settings.data ? 0 : 100 - settings.data.maxClaimPct;
  const marginPct = typedMargin !== null && typedMargin >= least && typedMargin <= 100 ? typedMargin : null;
  const settledPrice = useSettled(priceMinor, COST_MS);
  const settledMargin = useSettled(marginPct, COST_MS);
  const cost = useJobCost(clientId, settledPrice, settledMargin, null, paysAll);
  const costCurrent = cost.data !== undefined && !cost.isPlaceholderData && settledPrice === priceMinor && settledMargin === marginPct;
  return { least, marginPct, cost, costCurrent, create, createForTeam, createForInvoice };
}
